import { Fragment } from 'react';
import { Menu, MenuButton, MenuItem, MenuItems, Transition } from '@headlessui/react';
import { ChevronDown, Download } from 'lucide-react';
import type { DownloadFormat } from '../types';

interface DownloadMenuProps {
  onSelect: (format: DownloadFormat) => void;
  disabled?: boolean;
}

/** ダウンロード形式の単一ソース（表示順・ラベル・補足はここだけで定義する） */
const FORMATS: { format: DownloadFormat; label: string; hint: string }[] = [
  { format: 'html', label: 'HTML', hint: 'ブラウザで開ける整形済みの書き起こし' },
  { format: 'md', label: 'Markdown', hint: 'Obsidian・Notion などに貼り付け' },
  { format: 'txt', label: 'テキスト', hint: '装飾なしのプレーンテキスト' },
  { format: 'json', label: 'JSON', hint: 'タイムスタンプ・話者を含む構造化データ' },
];

/**
 * ダウンロード形式のドロップダウン。
 * 4形式を横並びボタンで出すと画面幅を占有し、主要導線（書き起こし本文）より目立ってしまうため、
 * 1つのボタンへ畳んでメニューで選ばせる。開閉・キーボード操作・外側クリックは Headless UI が担保する。
 */
export default function DownloadMenu({ onSelect, disabled = false }: DownloadMenuProps) {
  return (
    <Menu as="div" className="relative inline-block text-left">
      <MenuButton
        disabled={disabled}
        className="inline-flex items-center justify-center gap-2 font-medium rounded-control transition-colors
                   focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2
                   disabled:opacity-50 disabled:cursor-not-allowed
                   bg-primary text-white hover:bg-primary-hover px-4 py-2 text-sm"
      >
        <Download className="w-4 h-4" />
        ダウンロード
        <ChevronDown className="w-4 h-4" aria-hidden="true" />
      </MenuButton>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="opacity-0 scale-95"
        enterTo="opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="opacity-100 scale-100"
        leaveTo="opacity-0 scale-95"
      >
        <MenuItems className="absolute left-0 z-20 mt-2 w-64 origin-top-left rounded-panel bg-white shadow-card border border-gray-200 focus:outline-none overflow-hidden">
          {FORMATS.map(({ format, label, hint }) => (
            <MenuItem key={format}>
              {({ focus }) => (
                <button
                  type="button"
                  onClick={() => onSelect(format)}
                  className={`flex w-full flex-col items-start gap-0.5 px-4 py-2.5 text-left transition-colors ${
                    focus ? 'bg-primary/5' : ''
                  }`}
                >
                  <span className="text-sm font-medium text-text">{label}</span>
                  <span className="text-xs text-gray-500">{hint}</span>
                </button>
              )}
            </MenuItem>
          ))}
        </MenuItems>
      </Transition>
    </Menu>
  );
}
